import React from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useI18n } from "@/i18n";

// REAL product showcase. The four primary phones are ACTUAL screenshots of the
// live SDIP AI web app (Overview / Live / Performance / History) captured from
// production surfaces — no fabricated numbers. The ATLAS Assistant phone is a
// small, clearly illustrative preview (the assistant is behind login) that uses
// the current product design tokens and never presents fabricated performance.

interface Shot {
  src?: string;
  label: { tr: string; en: string };
  cls: string;
  illustrative?: boolean;
}

function PhoneShot({ shot }: { shot: Shot }) {
  const { lang } = useI18n();
  return (
    <div className="relative" aria-label={shot.label[lang]}>
      <div className="relative mx-auto w-[210px] rounded-[2.2rem] border border-white/12 bg-navy-900 p-2 shadow-panel ring-1 ring-white/5">
        <div className="absolute left-1/2 top-2 z-10 h-4 w-20 -translate-x-1/2 rounded-full bg-navy-950" />
        <div className="relative h-[420px] overflow-hidden rounded-[1.7rem] bg-navy-950">
          {shot.src ? (
            <img
              src={shot.src}
              alt={shot.label[lang]}
              loading="lazy"
              className="h-full w-full object-cover object-top"
              data-testid={`showcase-shot-${shot.label.en.toLowerCase().replace(/\s+/g, "-")}`}
            />
          ) : (
            <AtlasIllustrative />
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-center gap-2">
        <p className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">{shot.label[lang]}</p>
        {shot.illustrative && (
          <span className="rounded-full border border-white/12 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-muted-foreground">
            {lang === "tr" ? "Örnek" : "Preview"}
          </span>
        )}
      </div>
    </div>
  );
}

// Illustrative (non-live) ATLAS Assistant preview — uses real tokens, no fabricated stats.
function AtlasIllustrative() {
  const { lang } = useI18n();
  return (
    <div>
      <div className="flex items-center justify-between border-b border-white/5 px-3.5 py-3">
        <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-cyan-300"><Sparkles className="h-3.5 w-3.5" /> ATLAS Asistan</span>
        <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">SDIP AI</span>
      </div>
      <div className="space-y-2 p-3">
        <div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-atlas/15 px-3 py-2 text-[10px] text-foreground">
          {lang === "tr" ? "Bu maçta momentum kimde?" : "Who has the momentum?"}
        </div>
        <div className="mr-auto max-w-[85%] rounded-2xl rounded-tl-sm border border-white/8 bg-white/[0.03] px-3 py-2 text-[10px] text-foreground/90">
          {lang === "tr"
            ? "Son 15 dakikada baskı ev sahibine kaydı; şut ve korner üstünlüğü onlarda. Yine de skoru değiştirecek net bir fırsat oluşmadı."
            : "Pressure has shifted home in the last 15'; they lead on shots and corners, but no clear chance has materialised yet."}
        </div>
        <div className="mr-auto max-w-[70%] rounded-2xl rounded-tl-sm border border-gold/25 bg-gold/[0.06] px-3 py-2 text-[10px] text-gold-300">
          {lang === "tr" ? "Yeterli kanıt yok → değerlendirme üretilmez." : "Not enough evidence → no assessment produced."}
        </div>
      </div>
    </div>
  );
}

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" } };

export function RealShowcase() {
  const shots: Shot[] = [
    { src: "/product/app-overview.jpeg", label: { tr: "Genel Bakış", en: "Overview" }, cls: "lg:translate-y-6 lg:-rotate-2" },
    { src: "/product/app-live.jpeg", label: { tr: "Canlı Maç Merkezi", en: "Live Center" }, cls: "lg:-translate-y-2 z-10" },
    { src: "/product/app-performance.jpeg", label: { tr: "Performans", en: "Performance" }, cls: "lg:translate-y-4 lg:rotate-1" },
    { src: "/product/app-history.jpeg", label: { tr: "Çıktı Geçmişi", en: "Output History" }, cls: "lg:-translate-y-1" },
    { label: { tr: "ATLAS Asistan", en: "ATLAS Assistant" }, cls: "lg:translate-y-8 lg:rotate-2", illustrative: true },
  ];
  return (
    <div className="-mx-4 flex snap-x gap-6 overflow-x-auto px-4 pb-4 lg:mx-0 lg:grid lg:grid-cols-5 lg:items-center lg:gap-3 lg:overflow-visible lg:px-0" data-testid="real-showcase">
      {shots.map((s, i) => (
        <motion.div key={s.label.en} {...fade} transition={{ delay: i * 0.08, duration: 0.5 }} className={`shrink-0 snap-center ${s.cls}`}>
          <PhoneShot shot={s} />
        </motion.div>
      ))}
    </div>
  );
}
